import Color from './color';

// holds a mapping of ids to theme instances
const themes = {};

/**
 * Generate a theme id
 *
 * @param {Theme} theme A theme instance
 *
 * @return {Number}
 */
function themeId (theme, options) {
  if (theme.id) {
    return theme.id;
  }
  return Date.now() + parseInt(Math.random() * 1000, 10);
}

/**
 * Generate the stylesheet for a theme
 *
 * @param  {Theme} theme A theme instance
 *
 * @return {HTMLStyleElement}
 */
function themeStyle (theme) {
  const $style = document.createElement('style');

  $style.id = `iono-theme-${theme.id}`;

  return $style;
}

/**
 * Generate theme styles
 *
 * @param  {Theme} theme A theme instance
 *
 * @return {string}
 */
function themeTemplate (theme) {
  return `
    .${theme.className} .iono-selected {
      background: ${theme.getColor('background-light').toCss('hex')};
    }

    .${theme.className} .iono-select-option.iono-selected {
      color: ${theme.getColor('accent').toCss('hex')};
    }

    .${theme.className} .iono-slider-track {
      background-color: ${theme.getColor('text-light').toCss('hex')};
    }

    .${theme.className} .iono-slider-progress {
      background-color: ${theme.getColor('accent').toCss('hex')};
    }

    .${theme.className} .iono-slider-knob {
      background-color: ${theme.getColor('accent').toCss('hex')};
    }

    .${theme.className} .iono-theme-link {
      color: ${theme.getColor('link').toCss('hex')};
      text-decoration: underline;
    }

    .${theme.className} .iono-theme-link:hover {
      color: ${theme.getColor('link:hover').toCss('hex')};
    }

    .${theme.className} .iono-theme-border {
      border-color: ${theme.getColor('border').toCss('hex')}!important;
    }

    .${theme.className} .iono-theme-border-light {
      border-color: ${theme.getColor('border-light').toCss('hex')}!important;
    }

    .${theme.className} .iono-theme-border-dark {
      border-color: ${theme.getColor('border-dark').toCss('hex')}!important;
    }

    .${theme.className} .iono-theme-text,
    .${theme.className} .iono-theme-text\\@hover:hover,
    .${theme.className} .iono-theme-text\\@focus:focus {
      color: ${theme.getColor('text').toCss('hex')};
    }

    .${theme.className} .iono-theme-text-light,
    .${theme.className} .iono-theme-text-light\\@hover:hover,
    .${theme.className} .iono-theme-text-light\\@focus:focus {
      color: ${theme.getColor('text-light').toCss('hex')};
    }

    .${theme.className} .iono-theme-text-dark,
    .${theme.className} .iono-theme-text-dark\\@hover:hover,
    .${theme.className} .iono-theme-text-dark\\@focus:focus {
      color: ${theme.getColor('text-dark').toCss('hex')};
    }

    .${theme.className} .iono-theme-background,
    .${theme.className} .iono-theme-background\\@hover:hover,
    .${theme.className} .iono-theme-background\\@focus:focus {
      background-color: ${theme.getColor('background').toCss('hex')};
    }

    .${theme.className} .iono-theme-background-light,
    .${theme.className} .iono-theme-background-light\\@hover:hover,
    .${theme.className} .iono-theme-background-light\\@focus:focus {
      background-color: ${theme.getColor('background-light').toCss('hex')};
    }

    .${theme.className} .iono-theme-background-dark,
    .${theme.className} .iono-theme-background-dark\\@hover:hover,
    .${theme.className} .iono-theme-background-dark\\@focus:focus {
      background-color: ${theme.getColor('background-dark').toCss('hex')};
    }

    .${theme.className} .iono-theme-background-accent,
    .${theme.className} .iono-theme-background-accent\\@hover:hover,
    .${theme.className} .iono-theme-background-accent\\@focus:focus {
      background-color: ${theme.getColor('accent').toCss('hex')};
    }

    .${theme.className} .iono-theme-background-accent-light,
    .${theme.className} .iono-theme-background-accent-light\\@hover:hover,
    .${theme.className} .iono-theme-background-accent-light\\@focus:focus {
      background-color: ${theme.getColor('accent-light').toCss('hex')};
    }

    .${theme.className} .iono-theme-background-accent-dark,
    .${theme.className} .iono-theme-background-accent-dark\\@hover:hover,
    .${theme.className} .iono-theme-background-accent-dark\\@focus:focus {
      background-color: ${theme.getColor('accent-dark').toCss('hex')};
    }

    .${theme.className} .iono-theme-background-text,
    .${theme.className} .iono-theme-background-text\\@hover:hover,
    .${theme.className} .iono-theme-background-text\\@focus:focus {
      background-color: ${theme.getColor('text').toCss('hex')};
    }

    .${theme.className} .iono-theme-background-text-light,
    .${theme.className} .iono-theme-background-text-light\\@hover:hover,
    .${theme.className} .iono-theme-background-text-light\\@focus:focus {
      background-color: ${theme.getColor('text-light').toCss('hex')};
    }

    .${theme.className} .iono-theme-background-text-dark,
    .${theme.className} .iono-theme-background-text-dark\\@hover:hover,
    .${theme.className} .iono-theme-background-text-dark\\@focus:focus {
      background-color: ${theme.getColor('text-dark').toCss('hex')};
    }

    .${theme.className} .iono-theme-background-muted {
      background-color: ${theme.getColor('muted').toCss('hex')};
    }

    .${theme.className} .iono-theme-button {
      background: ${theme.getColor('background-light').toCss('hex')};
    }

    .${theme.className} .iono-theme-button:hover,
    .${theme.className} .iono-theme-button:focus {
      background: ${theme.getColor('background-dark').toCss('hex')};
    }

    .${theme.className} .iono-theme-button[disabled] {
      opacity: 0.8;
    }

    .${theme.className} .iono-icon-button {
      background: transparent;
      color: ${theme.getColor('text').toCss('hex')};
      fill: ${theme.getColor('text').toCss('hex')};
    }

    .${theme.className} .iono-icon-button:hover {
      color: ${theme.getColor('text-dark').toCss('hex')};
      fill: ${theme.getColor('text-dark').toCss('hex')};
    }

    .${theme.className} .iono-icon-button.iono-theme-accent {
      color: ${theme.getColor('accent').toCss('hex')};
      fill: ${theme.getColor('accent').toCss('hex')};
    }

    .${theme.className} .iono-icon-button.iono-theme-accent:hover {
      color: ${theme.getColor('accent-dark').toCss('hex')};
      fill: ${theme.getColor('accent-dark').toCss('hex')};
    }

    .${theme.className} .iono-theme-text-muted {
      color: ${theme.getColor('muted').toCss('hex')};
    }

    .${theme.className} .iono-theme-text-accent {
      color: ${theme.getColor('accent').toCss('hex')};
    }

    .${theme.className} .iono-theme-logo > svg {
      color: ${theme.getColor('accent').toCss('hex')};
      fill: ${theme.getColor('text').toCss('hex')};
    }

    .${theme.className} .iono-theme-rounded {
      border-radius: ${theme.rounded}px;
    }

    .${theme.className} .iono-theme-overlay {
      background-color: ${theme.getColor('background').withAlpha(0.5).toCss('rgba')};
    }

    .${theme.className} .iono-theme-icon-accent {
      color: ${theme.getColor('accent').toCss('hex')};
      fill: ${theme.getColor('accent').toCss('hex')};
    }
  `;
}

/**
 * Validate that the given color is a valid hex code. Valid representations include:
 *
 *  - #000000 or 000000
 *  - #000 or 000
 *
 * @param  {String} color A hex code for a color
 * @return {Boolean}
 */
function validateThemeColor (color) {
  if (!color) return false;
  if (color.match(/^#?([0-9a-fA-F]{3}){1,2}$/) === null) return false;
  return true;
}

function contrastRatio (a, b) {
  // (L1 + 0.05) / (L2 + 0.05)
  const La = a.luminance() + 0.05;
  const Lb = b.luminance() + 0.05;

  if (Lb > La) {
    return Lb / La;
  }
  return La / Lb;
}

function generate (base, maxIterations = 10) {
  const l = base.luminance(); // 0 - 1

  let light = base.clone();
  let lightContrastRatio = 1;
  let lightIterations = 0;
  while (lightContrastRatio <= 1.2) {
    if (l < 0.1) {
      light = light.lighten(10);
    } else if (l > 0.9) {
      light = light.darken(5);
    } else {
      light = light.lighten(5);
    }
    lightContrastRatio = contrastRatio(base, light);
    if (lightIterations++ > maxIterations) {
      break;
    }
  }

  let dark = base.clone();
  let darkContrastRatio = 1;
  let darkIterations = 0;
  while (darkContrastRatio <= 1.5) {
    if (l < 0.1) {
      dark = dark.lighten(5);
    } else if (l > 0.9) {
      dark = dark.darken(10);
    } else {
      dark = dark.darken(5);
    }
    darkContrastRatio = contrastRatio(base, dark);
    if (darkIterations++ > maxIterations) {
      break;
    }
  }

  return {
    base,
    light,
    dark
  };
}

/**
 * Builds the options object passed to the Theme constructor from ui options
 *
 * @param  {Object} theme  The ui.theme option passed to the player on creation
 * @param  {Layout} layout The layout this theme is being rendered for
 * @return {Object}
 */
export function buildThemeOptions (theme, layout) {
  const options = {
    id: layout.player.id,
    rounded: theme.rounded,
    colors: {}
  };

  if (theme.text) {
    options.colors.text = theme.text;
  }

  if (theme.background) {
    options.colors.background = theme.background;
  }

  if (theme.accent) {
    options.colors.accent = theme.accent;
  }

  if (theme.border) {
    options.colors.border = theme.border;
  }

  return options;
}

export default class Theme {
  // retrieve the className to apply to DOM elements to scope them to this theme
  get className () {
    return `iono-player-${this.id}`;
  }

  /**
   * Constructor.
   *
   * @param  {Object} options Theme options
   */
  constructor (options) {
    this.id = themeId(this, options);
    themes[this.id] = this;

    this.colors = {};
    this.$style = themeStyle(this);

    this.setRounded(options.rounded, false);
    this.setColors(options.colors, false);

    this.update();
  }

  /**
   * Retrieve a theme color
   *
   * @param  {string} key The theme color key
   *
   * @return {Color}
   */
  getColor (key) {
    return this.colors[key];
  }

  /**
   * Change a theme color
   *
   * @param {String}  key    The color key to be changed
   * @param {String}  color  The new color value
   * @param {Boolean} update Update the stylesheet after changing color
   *
   * @return {self}
   */
  setColor (key, color, update = true) {
    if (color instanceof Color) {
      this.colors[key] = color;
    } else {
      if (validateThemeColor(color)) {
        this.colors[key] = Color.parse(color);
      } else {
        return this;
      }
    }

    // update the theme
    if (update) {
      this.update();
    }

    return this;
  }

  /**
   * Change the theme colors
   *
   * @param {Object}  colors A mapping of color keys to a color value
   * @param {Boolean} update Update the stylesheet after changing colors
   *
   * @return {self}
   */
  setColors (colors, update = true) {
    for (const key in colors) {
      this.setColor(key, colors[key], false);
    }

    const background = this.getColor('background');
    const text = this.getColor('text');

    if (!colors.border && background) {
      this.setColor('border', background.blend(text.withAlpha(0.3)), false);
    }

    if (background && text) {
      if (!colors.muted) {
        this.setColor('muted', background.blend(text.withAlpha(0.8)).toCss('hex'), false);
      }

      if (!colors.link) {
        this.setColor('link', background.blend(text.withAlpha(0.8)).toCss('hex'), false);
        this.setColor('link:hover', text.toCss('hex'), false);
      }
    }

    if (update) {
      this.update();
    }

    return this;
  }

  getRounded () {
    return this.rounded;
  }

  /**
   * Change the rounded option
   *
   * @param {Number} amount
   *
   * @return {self}
   */
  setRounded (amount, update = true) {
    if (typeof amount !== 'number') {
      amount = parseInt(amount, 10);
    }

    this.rounded = Math.max(0, Math.min(20, amount));

    if (update) {
      this.update();
    }

    return this;
  }

  /**
   * Render the theme stylesheet contents
   *
   * @return {self}
   */
  render () {
    return themeTemplate(this);
  }

  /**
   * Update the themes stylesheet
   *
   * @return {self}
   */
  update () {
    const background = generate(this.colors.background);
    const text = generate(this.colors.text);
    const border = generate(this.colors.border);
    const accent = generate(this.colors.accent);

    this.colors['accent-light'] = accent.light;
    this.colors['accent-dark'] = accent.dark;
    this.colors['background-light'] = background.light;
    this.colors['background-dark'] = background.dark;
    this.colors['text-light'] = text.light;
    this.colors['text-dark'] = text.dark;
    this.colors['border-light'] = border.light;
    this.colors['border-dark'] = border.dark;

    this.$style.innerHTML = this.render();
    return this;
  }
}
